const BASE_URL    = process.env.REACT_APP_OAUTH_BASE_URL;
const CLIENT_ID   = process.env.REACT_APP_OAUTH_CLIENT_ID;
const REDIRECT_URI = process.env.REACT_APP_OAUTH_REDIRECT_URI;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOKEN_KEY   = 'sf_access_token';
const REFRESH_KEY = 'sf_refresh_token';

// ── PKCE 헬퍼 ────────────────────────────────────────────────────────
async function sha256(plain) {
  const enc  = new TextEncoder().encode(plain);
  const hash = await crypto.subtle.digest('SHA-256', enc);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function randomString(len = 64) {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('').slice(0, len);
}

// ── 로그인 시작 (PKCE Authorization Code Flow) ───────────────────────
export async function startAuthFlow() {
  const verifier  = randomString(64);
  const challenge = await sha256(verifier);
  const state     = randomString(16);

  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id:             CLIENT_ID,
    response_type:         'code',
    redirect_uri:          REDIRECT_URI,
    scope:                 'basic profile',
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
  });
  window.location.href = `${BASE_URL}/oauth/authorize?${params}`;
}

// ── 콜백 처리 ────────────────────────────────────────────────────────
export async function handleCallback() {
  const params   = new URLSearchParams(window.location.search);
  const code     = params.get('code');
  const state    = params.get('state');
  const verifier = sessionStorage.getItem('pkce_verifier');

  if (!code) throw new Error('인가 코드가 없습니다.');
  if (state !== sessionStorage.getItem('oauth_state')) throw new Error('State mismatch');

  const res = await fetch(`${BACKEND_URL}/api/oauth/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ code, redirect_uri: REDIRECT_URI, code_verifier: verifier }),
  });

  const tokens = await res.json();
  if (!tokens.access_token) throw new Error(tokens.error || '토큰 발급 실패');

  localStorage.setItem(TOKEN_KEY,   tokens.access_token);
  if (tokens.refresh_token) localStorage.setItem(REFRESH_KEY, tokens.refresh_token);

  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('oauth_state');

  return tokens;
}

// ── 사용자 정보 조회 ─────────────────────────────────────────────────
export async function getUserInfo() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  const res = await fetch(`${BACKEND_URL}/api/oauth/userinfo`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.json();
}

// ── 토큰 자동 갱신 fetch 래퍼 ────────────────────────────────────────
export async function authFetch(url, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res   = await fetch(url, {
    ...options,
    headers: { ...options.headers, 'Authorization': `Bearer ${token}` },
  });

  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) throw new Error('세션 만료 - 재로그인 필요');
    return authFetch(url, options);
  }
  return res;
}

async function refreshAccessToken() {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return false;

  try {
    const res    = await fetch(`${BACKEND_URL}/api/oauth/refresh`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refresh_token: refresh }),
    });
    const tokens = await res.json();
    if (!tokens.access_token) return false;
    localStorage.setItem(TOKEN_KEY,   tokens.access_token);
    if (tokens.refresh_token) localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
    return true;
  } catch {
    return false;
  }
}

// ── 로그아웃 ─────────────────────────────────────────────────────────
export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
